import React, { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import useApiEp from "../../../hooks/useApiEp";
import LoadingWidget from "../../../widgets/LoadingWidget";
import {
  Card,
  CardContent,
  Typography,
  Grid,
  Alert,
  Box,
  Chip,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  CircularProgress,
  Container,
  Stack,
} from "@mui/material";
import { format } from "date-fns";
import { FaRobot, FaCheckCircle, FaCloudUploadAlt } from "react-icons/fa";
import { styled } from "@mui/material/styles";
import BackBtn from "../../../widgets/BackBtn";
import { getHeader, toastError } from "../../../utils/util";
import { Edit } from "@mui/icons-material";
import axios from "axios";
import { API_ENDPOINT } from "../../../utils/constants";
import { toast } from "react-toastify";

const StyledCard = styled(Card)(({ theme }) => ({
  borderRadius: theme.shape.borderRadius * 2,
  boxShadow: theme.shadows[1],
  transition: "transform 0.2s ease-in-out",
  "&:hover": {
    transform: "translateY(-4px)",
  },
}));

const StyledButton = styled(Button)(({ theme }) => ({
  borderRadius: theme.shape.borderRadius,
  textTransform: "none",
  fontWeight: 600,
  padding: theme.spacing(1, 3),
}));

const ChatbotFlowDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const {
    data: chatbotData,
    isLoading,
    isError,
    error,
    refetch,
  } = useApiEp(`/message/get-flow-detail/${id}`);
  const [openDialog, setOpenDialog] = useState(false);
  const [isPublishing, setIsPublishing] = useState(false);

  const handlePublishClick = () => {
    setOpenDialog(true);
  };

  const handleConfirmPublish = async () => {
    setIsPublishing(true);
    try {
      const response = await axios.post(
        `${API_ENDPOINT}/message/publish-chatbot-flow/${id}`,
        {},
        getHeader()
      );
      refetch();
      toast.success(response?.data?.message);
      setOpenDialog(false);
    } catch (err) {
      toastError(error);
    } finally {
      setIsPublishing(false);
    }
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
  };

  if (isLoading) {
    return <LoadingWidget label="Loading details..." />;
  }

  if (isError) {
    return (
      <Alert
        severity="error"
        sx={{ m: 2, color: "error.contrastText", bgcolor: "error.main" }}
      >
        Error loading flow details: {error?.message || "Unknown error"}
      </Alert>
    );
  }

  if (!chatbotData) {
    return (
      <Alert
        severity="warning"
        sx={{ m: 2, color: "warning.contrastText", bgcolor: "warning.main" }}
      >
        No flow details found
      </Alert>
    );
  }

  return (
    <Container maxWidth="md" sx={{ mt: 3 }}>
      <BackBtn />
      <Box sx={{ maxWidth: 600, mx: "auto", my: 4 }}>
        <StyledCard>
          {chatbotData.status === "draft" && (
            <Box
              sx={{
                p: 2,
                bgcolor: "warning.light",
                backgroundImage: "linear-gradient(to right, #fff3e0, #ffecd2)",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
              }}
            >
              <Typography
                variant="body2"
                sx={{ color: "warning.dark", fontWeight: "medium" }}
              >
                Your flow is in Draft mode. Publish to make it live!
              </Typography>
              <StyledButton
                variant="contained"
                color="primary"
                startIcon={<FaCloudUploadAlt />}
                onClick={handlePublishClick}
                disabled={isPublishing}
                sx={{ bgcolor: "primary.main", color: "primary.contrastText" }}
              >
                {isPublishing ? <CircularProgress size={24} /> : "Publish Flow"}
              </StyledButton>
            </Box>
          )}

          <CardContent>
            <Box sx={{ display: "flex", alignItems: "center", mb: 3, gap: 2 }}>
              <FaRobot size={24} style={{ color: "#1976d2", flexShrink: 0 }} />
              <Typography
                variant="h5"
                sx={{ color: "text.primary", fontWeight: "bold", flexGrow: 1 }}
              >
                Chatbot Flow Details
              </Typography>

              <Button
                onClick={() => {
                  navigate(`/home/${id}/chatbot-flow-builder`);
                }}
                startIcon={<Edit />}
                variant="outlined"
                size="small"
                sx={{ flexShrink: 0 }}
              >
                Update
              </Button>
            </Box>
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <Typography
                  variant="subtitle2"
                  sx={{ color: "text.secondary" }}
                >
                  Flow ID
                </Typography>
                <Typography
                  variant="body1"
                  sx={{ color: "text.primary", fontWeight: "medium" }}
                >
                  {chatbotData._id}
                </Typography>
              </Grid>
              <Grid item xs={12}>
                <Typography
                  variant="subtitle2"
                  sx={{ color: "text.secondary" }}
                >
                  Flow Name
                </Typography>
                <Typography
                  variant="body1"
                  sx={{ color: "text.primary", fontWeight: "medium" }}
                >
                  {chatbotData.flowName}
                </Typography>
              </Grid>
              <Grid item xs={12}>
                <Typography
                  variant="subtitle2"
                  sx={{ color: "text.secondary" }}
                >
                  Flow Starter
                </Typography>
                <Typography
                  variant="body1"
                  sx={{ color: "text.primary", fontWeight: "medium" }}
                >
                  {chatbotData.flowStarter}
                </Typography>
              </Grid>
              <Grid item xs={12}>
                <Typography
                  variant="subtitle2"
                  sx={{ color: "text.secondary" }}
                >
                  Status
                </Typography>
                {chatbotData.status === "draft" ? (
                  <Chip
                    icon={<FaCloudUploadAlt />}
                    label="Draft"
                    color="warning"
                    size="small"
                    sx={{
                      fontWeight: "medium",
                      color: "warning.contrastText",
                      bgcolor: "warning.main",
                    }}
                  />
                ) : (
                  <Chip
                    icon={<FaCheckCircle />}
                    label="Published"
                    color="success"
                    size="small"
                    sx={{
                      fontWeight: "medium",
                      color: "success.contrastText",
                      bgcolor: "success.main",
                    }}
                  />
                )}
              </Grid>
              <Grid item xs={6}>
                <Typography
                  variant="subtitle2"
                  sx={{ color: "text.secondary" }}
                >
                  Node Count
                </Typography>
                <Typography
                  variant="body1"
                  sx={{ color: "text.primary", fontWeight: "medium" }}
                >
                  {chatbotData?.nodes?.length}
                </Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography
                  variant="subtitle2"
                  sx={{ color: "text.secondary" }}
                >
                  Edge Count
                </Typography>
                <Typography
                  variant="body1"
                  sx={{ color: "text.primary", fontWeight: "medium" }}
                >
                  {chatbotData?.edges?.length}
                </Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography
                  variant="subtitle2"
                  sx={{ color: "text.secondary" }}
                >
                  Created On
                </Typography>
                <Typography
                  variant="body1"
                  sx={{ color: "text.primary", fontWeight: "medium" }}
                >
                  {format(new Date(chatbotData.createdOn), "PPp")}
                </Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography
                  variant="subtitle2"
                  sx={{ color: "text.secondary" }}
                >
                  Updated On
                </Typography>
                <Typography
                  variant="body1"
                  sx={{ color: "text.primary", fontWeight: "medium" }}
                >
                  {format(new Date(chatbotData.updatedOn), "PPp")}
                </Typography>
              </Grid>
            </Grid>
          </CardContent>
        </StyledCard>

        <Dialog
          open={openDialog}
          onClose={handleCloseDialog}
          aria-labelledby="publish-dialog-title"
        >
          <DialogTitle id="publish-dialog-title" sx={{ color: "text.primary" }}>
            Confirm Publish
          </DialogTitle>
          <DialogContent>
            <DialogContentText sx={{ color: "text.primary" }}>
              Are you sure you want to publish the flow "{chatbotData.flowName}
              "? This action will make it live and accessible.
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button
              onClick={handleCloseDialog}
              sx={{ color: "text.secondary" }}
            >
              Cancel
            </Button>
            <StyledButton
              onClick={handleConfirmPublish}
              variant="contained"
              disabled={isPublishing}
              sx={{ bgcolor: "primary.main", color: "primary.contrastText" }}
            >
              {isPublishing ? <CircularProgress size={24} /> : "Confirm"}
            </StyledButton>
          </DialogActions>
        </Dialog>
      </Box>
    </Container>
  );
};

export default ChatbotFlowDetails;
